import { identityApiRef, useApi } from '@mainstage/core-plugin-api';
import { catalogApiRef } from '@mainstage/plugin-catalog-react';
import { useQuery } from '@tanstack/react-query';
import pLimit from 'p-limit';
import { teamRolesApiRef } from '../plugin';
import {
  getTeamRoleAssignmentKindEnum,
  TeamRoleAssignment,
  TeamRoleAssignmentKindEnum,
} from '../types';
import {
  ResolveTeamRoleParams,
  TeamRolesResolveResponse,
} from '@agilelab/plugin-wb-team-roles-common';
import { QUERY_KEY_TEAM_ROLE_ASSIGNMENTS } from '../constants';

export const useTeamRoleAssignments = (params: ResolveTeamRoleParams) => {
  const identityApi = useApi(identityApiRef);
  const teamRolesApi = useApi(teamRolesApiRef);
  const catalogApi = useApi(catalogApiRef);

  return useQuery<{ assignments: TeamRoleAssignment[]; total: number }, Error>({
    queryKey: [
      QUERY_KEY_TEAM_ROLE_ASSIGNMENTS,
      params.teamRole,
      params.projectUrn,
      params.mode,
      params.useFallback,
    ],
    queryFn: async () => {
      const options = await identityApi.getCredentials();
      const response: TeamRolesResolveResponse = await teamRolesApi.resolve({
        ...params,
        options: options,
      });

      // Grouping subject and their kinds (owner, delegate)
      const groupedMap = response.subjects.reduce((map, s) => {
        const kind = getTeamRoleAssignmentKindEnum(s.exactMatch, s.isLimited);

        if (!map.has(s.subject)) {
          map.set(s.subject, new Set<TeamRoleAssignmentKindEnum>());
        }

        map.get(s.subject)!.add(kind);

        return map;
      }, new Map<string, Set<TeamRoleAssignmentKindEnum>>());

      const displayNameMap = new Map<string, string>();

      const entityRefsArray = [...groupedMap.keys()];
      const batchSize = 25;
      const batches: string[][] = [];
      for (let i = 0; i < entityRefsArray.length; i += batchSize) {
        batches.push(entityRefsArray.slice(i, i + batchSize));
      }

      // --- Step 3: Fetch entities in batches
      const limit = pLimit(2); // Limit to 2 concurrent batch requests
      await Promise.all(
        batches.map(batch =>
          limit(async () => {
            try {
              const entitiesByRefsresponse = await catalogApi.getEntitiesByRefs(
                { entityRefs: batch, fields: ['spec.profile'] },
                options,
              );
              // Map displaynames to their original refs
              entitiesByRefsresponse.items.forEach((entity, index) => {
                if (entity) {
                  const entityRef = batch[index];
                  const displayName =
                    (entity?.spec?.profile as any)?.displayName ?? entityRef;
                  displayNameMap.set(entityRef, displayName);
                }
              });
            } catch (e) {
              // Skip resolution for missing entities in this batch
              return;
            }
          }),
        ),
      );

      const assignments: TeamRoleAssignment[] = Array.from(
        groupedMap,
        ([subject, kindsSet]) => ({
          subject: {
            urn: subject,
            displayName: displayNameMap.get(subject) ?? subject,
          },
          kinds: Array.from(kindsSet),
        }),
      );

      return {
        assignments: assignments,
        total: response.total,
      };
    },
  });
};
